// src/components/AdminPanel.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  VStack,
  HStack,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Switch,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useToast,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Progress,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Icon,
  Flex
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon, ViewIcon } from '@chakra-ui/icons';
import { MdLoop, MdPhone, MdMoneyOff, MdAttachMoney } from 'react-icons/md';
import { db, auth } from '../firebase';
import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc, 
  addDoc,
  query,
  orderBy,
  limit,
  where,
  getDoc
} from 'firebase/firestore';

// Call pricing configuration מעודכן
const CALL_PRICING = {
  per_minute_answered_usd: 0.29,    // $0.29 לדקה על שיחות שנענו
  minimum_charge_usd: 0.29,        // מינימום דקה אחת
  digging_mode_cost: 0.00,         // מצב חפירה - ללא חיוב
  currency: 'USD'
};

const AdminPanel = () => {
  const [users, setUsers] = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [billingLogs, setBillingLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditAmount, setCreditAmount] = useState(0);
  const { isOpen: isCreditOpen, onOpen: onCreditOpen, onClose: onCreditClose } = useDisclosure();
  const { isOpen: isFeatureOpen, onOpen: onFeatureOpen, onClose: onFeatureClose } = useDisclosure();
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch users
      const usersQuery = query(collection(db, 'users'), orderBy('created_at', 'desc'));
      const usersSnapshot = await getDocs(usersQuery);
      const usersData = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUsers(usersData);

      // Fetch recent call logs
      const callsQuery = query(
        collection(db, 'call_logs'), 
        orderBy('started_at', 'desc'), 
        limit(100)
      );
      const callsSnapshot = await getDocs(callsQuery);
      const callsData = callsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCallLogs(callsData);

      // Fetch recent billing logs
      const billingQuery = query(
        collection(db, 'billing_logs'), 
        orderBy('timestamp', 'desc'), 
        limit(50)
      );
      const billingSnapshot = await getDocs(billingQuery);
      const billingData = billingSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBillingLogs(billingData);

    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast({
        title: 'שגיאה',
        description: 'נכשל בטעינת נתוני האדמין',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  };

  const addCredits = async () => {
    if (!selectedUser || creditAmount <= 0) return;

    try {
      const userRef = doc(db, 'users', selectedUser.id);
      const currentCredits = selectedUser.credits || 0;
      const newCredits = currentCredits + creditAmount;

      // Update user credits
      await updateDoc(userRef, {
        credits: newCredits,
        updated_at: new Date().toISOString()
      });

      // Log the transaction
      await addDoc(collection(db, 'billing_logs'), {
        user_id: selectedUser.id,
        type: 'credit_added',
        amount: creditAmount,
        description: `זיכוי ידני של ${creditAmount} דולר`,
        previous_balance: currentCredits,
        new_balance: newCredits,
        timestamp: new Date().toISOString(),
        admin_id: auth.currentUser.uid
      });

      // Update local state
      setUsers(users.map(user => 
        user.id === selectedUser.id 
          ? { ...user, credits: newCredits }
          : user
      ));

      onCreditClose();
      setCreditAmount(0);
      setSelectedUser(null);

      toast({
        title: 'הזיכוי בוצע בהצלחה',
        description: `נוספו $${creditAmount} ליתרה`,
        status: 'success',
        duration: 3000,
        isClosable: true
      });

      fetchData(); // Refresh data

    } catch (error) {
      console.error('Error adding credits:', error);
      toast({
        title: 'שגיאה',
        description: 'נכשל בהוספת זיכוי',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    }
  };

  const toggleFeature = async (userId, featureName, currentValue) => {
    try {
      const userRef = doc(db, 'users', userId);
      const updates = {
        [`features.${featureName}`]: !currentValue,
        updated_at: new Date().toISOString()
      };

      await updateDoc(userRef, updates);

      // Update local state
      setUsers(users.map(user => 
        user.id === userId 
          ? {
              ...user, 
              features: {
                ...user.features,
                [featureName]: !currentValue
              }
            }
          : user
      ));

      toast({
        title: 'הפיצ\'ר עודכן',
        description: `${featureName} ${!currentValue ? 'הופעל' : 'בוטל'}`,
        status: 'success',
        duration: 2000,
        isClosable: true
      });

    } catch (error) {
      console.error('Error toggling feature:', error);
      toast({
        title: 'שגיאה',
        description: 'נכשל בעדכון הפיצ\'ר',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    }
  };

  // Statistics calculations מעודכן עם מצב חפירה
  const getSystemStats = () => {
    const totalUsers = users.length;
    const totalCredits = users.reduce((sum, user) => sum + (user.credits || 0), 0);
    const totalCalls = callLogs.length;
    const successfulCalls = callLogs.filter(call => call.success).length;
    
    // הבחנה בין מצבי שיחה
    const normalCalls = callLogs.filter(call => call.mode !== 'digging').length;
    const diggingCalls = callLogs.filter(call => call.mode === 'digging').length;
    const chargedCalls = callLogs.filter(call => call.cost_usd > 0).length;
    const freeCalls = callLogs.filter(call => call.cost_usd === 0 || call.mode === 'digging').length;
    
    const totalRevenue = billingLogs
      .filter(log => log.type === 'call_charge')
      .reduce((sum, log) => sum + log.amount, 0);

    return {
      totalUsers,
      totalCredits,
      totalCalls,
      normalCalls,
      diggingCalls,
      successfulCalls,
      chargedCalls,
      freeCalls,
      successRate: totalCalls ? Math.round((successfulCalls / totalCalls) * 100) : 0,
      totalRevenue
    };
  };

  const stats = getSystemStats();

  const formatCurrency = (amount) => `$${amount?.toFixed(2) || '0.00'}`;
  const formatDateTime = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleString('he-IL');
    } catch {
      return 'N/A';
    }
  };

  const getCallModeDisplay = (mode) => {
    switch (mode) {
      case 'digging':
        return (
          <Badge colorScheme="purple" fontSize="xs" borderRadius="md">
            <Icon as={MdLoop} mr={1} />
            חפירה
          </Badge>
        );
      case 'normal':
      default:
        return (
          <Badge colorScheme="blue" fontSize="xs" borderRadius="md">
            <Icon as={MdPhone} mr={1} />
            רגיל
          </Badge>
        );
    }
  };

  const getCallCostDisplay = (call) => {
    if (call.mode === 'digging') {
      return (
        <Text color="green.500" fontWeight="medium">
          <Icon as={MdMoneyOff} mr={1} />
          ללא חיוב
        </Text>
      );
    } else if (call.cost_usd > 0) {
      return (
        <Text color="red.500" fontWeight="medium">
          <Icon as={MdAttachMoney} mr={1} />
          {formatCurrency(call.cost_usd)}
        </Text>
      );
    } else {
      return (
        <Text color="green.500" fontWeight="medium">
          <Icon as={MdMoneyOff} mr={1} />
          ללא חיוב
        </Text>
      );
    }
  };

  return (
    <Box p={6}>
      <Heading mb={6} color="teal.600">
        פאנל מנהל מערכת
      </Heading>

      <Tabs variant="enclosed" colorScheme="teal">
        <TabList>
          <Tab>סקירה כללית</Tab>
          <Tab>ניהול משתמשים</Tab>
          <Tab>לוג שיחות</Tab>
          <Tab>היסטוריית חיובים</Tab>
          <Tab>הגדרות מערכת</Tab>
        </TabList>

        <TabPanels>
          {/* סקירה כללית - מעודכן */}
          <TabPanel>
            <VStack spacing={6} align="stretch">
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                <Stat bg={bgColor} p={4} borderRadius="md" borderWidth={1}>
                  <StatLabel>סה"כ משתמשים</StatLabel>
                  <StatNumber>{stats.totalUsers}</StatNumber>
                  <StatHelpText>רשומים במערכת</StatHelpText>
                </Stat>
                <Stat bg={bgColor} p={4} borderRadius="md" borderWidth={1}>
                  <StatLabel>יתרה כוללת</StatLabel>
                  <StatNumber color="green.500">{formatCurrency(stats.totalCredits)}</StatNumber>
                  <StatHelpText>של כל המשתמשים</StatHelpText>
                </Stat>
                <Stat bg={bgColor} p={4} borderRadius="md" borderWidth={1}>
                  <StatLabel>הכנסות כוללות</StatLabel>
                  <StatNumber color="blue.500">{formatCurrency(stats.totalRevenue)}</StatNumber>
                  <StatHelpText>מתשלומי שיחות</StatHelpText>
                </Stat>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <Stat bg={bgColor} p={4} borderRadius="md" borderWidth={1}>
                  <StatLabel>סה"כ שיחות</StatLabel>
                  <StatNumber>{stats.totalCalls}</StatNumber>
                  <StatHelpText>
                    <VStack align="start" spacing={1}>
                      <HStack>
                        <Icon as={MdPhone} color="blue.500" />
                        <Text as="span" color="blue.500">
                          {stats.normalCalls} רגילות
                        </Text>
                      </HStack>
                      <HStack>
                        <Icon as={MdLoop} color="purple.500" />
                        <Text as="span" color="purple.500">
                          {stats.diggingCalls} חפירה
                        </Text>
                      </HStack>
                    </VStack>
                  </StatHelpText>
                </Stat>
                <Stat bg={bgColor} p={4} borderRadius="md" borderWidth={1}>
                  <StatLabel>שיעור הצלחה</StatLabel>
                  <StatNumber>{stats.successRate}%</StatNumber>
                  <StatHelpText>
                    <Progress 
                      value={stats.successRate} 
                      colorScheme="green" 
                      size="sm" 
                      mt={2}
                    />
                    <HStack spacing={4} mt={2} fontSize="sm">
                      <HStack>
                        <Icon as={MdAttachMoney} color="red.500" />
                        <Text color="red.500">{stats.chargedCalls} חויבו</Text>
                      </HStack>
                      <HStack>
                        <Icon as={MdMoneyOff} color="green.500" />
                        <Text color="green.500">{stats.freeCalls} ללא חיוב</Text>
                      </HStack>
                    </HStack>
                  </StatHelpText>
                </Stat>
              </SimpleGrid>

              {/* סטטיסטיקות נוספות על מצבי שיחה */}
              <Card variant="outline" borderColor={borderColor}>
                <CardHeader>
                  <Heading size="md">פילוח לפי מצב שיחה</Heading>
                </CardHeader>
                <CardBody>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <Box>
                      <Flex align="center" mb={2}>
                        <Icon as={MdPhone} color="blue.500" mr={2} />
                        <Text fontWeight="semibold">שיחות רגילות</Text>
                      </Flex>
                      <Text fontSize="2xl" fontWeight="bold" color="blue.500">
                        {stats.normalCalls}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        עם חיבור לסוכן - חיוב בהתאם
                      </Text>
                    </Box>
                    <Box>
                      <Flex align="center" mb={2}>
                        <Icon as={MdLoop} color="purple.500" mr={2} />
                        <Text fontWeight="semibold">שיחות חפירה</Text>
                      </Flex>
                      <Text fontSize="2xl" fontWeight="bold" color="purple.500">
                        {stats.diggingCalls}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        ללא סוכן - ללא חיוב כלל
                      </Text>
                    </Box>
                  </SimpleGrid>
                </CardBody>
              </Card>
            </VStack>
          </TabPanel>

          {/* ניהול משתמשים - אותו דבר */}
          <TabPanel>
            <Card variant="outline" borderColor={borderColor}>
              <CardHeader>
                <Heading size="md">ניהול משתמשים ופיצ'רים</Heading>
              </CardHeader>
              <CardBody>
                <Box overflowX="auto">
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>שם משתמש</Th>
                        <Th>אימייל</Th>
                        <Th>יתרה</Th>
                        <Th>תפקיד</Th>
                        <Th>פיצ'רים</Th>
                        <Th>פעולות</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {users.map((user) => (
                        <Tr key={user.id}>
                          <Td fontWeight="medium">{user.name || 'לא צוין'}</Td>
                          <Td>{user.email}</Td>
                          <Td>
                            <Text color="green.500" fontWeight="bold">
                              {formatCurrency(user.credits)}
                            </Text>
                          </Td>
                          <Td>
                            <Badge 
                              colorScheme={user.role === 'admin' ? 'red' : 'blue'}
                            >
                              {user.role === 'admin' ? 'מנהל' : 'משתמש'}
                            </Badge>
                          </Td>
                          <Td>
                            <HStack spacing={2} wrap="wrap">
                              {Object.entries(user.features || {}).map(([feature, enabled]) => (
                                <Badge 
                                  key={feature}
                                  colorScheme={enabled ? 'green' : 'gray'}
                                  variant={enabled ? 'solid' : 'outline'}
                                  fontSize="xs"
                                >
                                  {feature}
                                </Badge>
                              ))}
                            </HStack>
                          </Td>
                          <Td>
                            <HStack spacing={2}>
                              <Button
                                size="sm"
                                colorScheme="green"
                                leftIcon={<AddIcon />}
                                onClick={() => {
                                  setSelectedUser(user);
                                  onCreditOpen();
                                }}
                              >
                                זיכוי
                              </Button>
                              <Button
                                size="sm"
                                colorScheme="blue"
                                leftIcon={<EditIcon />}
                                onClick={() => {
                                  setSelectedUser(user);
                                  onFeatureOpen();
                                }}
                              >
                                פיצ'רים
                              </Button>
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </CardBody>
            </Card>
          </TabPanel>

          {/* לוג שיחות - מעודכן עם מצב חפירה */}
          <TabPanel>
            <Card variant="outline" borderColor={borderColor}>
              <CardHeader>
                <Heading size="md">היסטוריית שיחות</Heading>
              </CardHeader>
              <CardBody>
                <Box overflowX="auto">
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>משתמש</Th>
                        <Th>למספר</Th>
                        <Th>מצב</Th>
                        <Th>סטטוס</Th>
                        <Th>עלות</Th>
                        <Th>ניסיונות</Th>
                        <Th>זמן התחלה</Th>
                        <Th>זמן סיום</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {callLogs.map((call) => {
                        const user = users.find(u => u.id === call.user_id);
                        return (
                          <Tr key={call.id}>
                            <Td>{user?.email || call.user_id}</Td>
                            <Td>{call.to_number}</Td>
                            <Td>{getCallModeDisplay(call.mode)}</Td>
                            <Td>
                              <Badge 
                                colorScheme={call.success ? 'green' : 'red'}
                              >
                                {call.status}
                              </Badge>
                            </Td>
                            <Td>{getCallCostDisplay(call)}</Td>
                            <Td>{call.attempts}</Td>
                            <Td fontSize="sm">{formatDateTime(call.started_at)}</Td>
                            <Td fontSize="sm">{formatDateTime(call.ended_at)}</Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>
              </CardBody>
            </Card>
          </TabPanel>

          {/* היסטוריית חיובים - אותו דבר */}
          <TabPanel>
            <Card variant="outline" borderColor={borderColor}>
              <CardHeader>
                <Heading size="md">היסטוריית עסקאות</Heading>
              </CardHeader>
              <CardBody>
                <Box overflowX="auto">
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>משתמש</Th>
                        <Th>סוג עסקה</Th>
                        <Th>סכום</Th>
                        <Th>יתרה קודמת</Th>
                        <Th>יתרה חדשה</Th>
                        <Th>תיאור</Th>
                        <Th>זמן</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {billingLogs.map((log) => {
                        const user = users.find(u => u.id === log.user_id);
                        return (
                          <Tr key={log.id}>
                            <Td>{user?.email || log.user_id}</Td>
                            <Td>
                              <Badge 
                                colorScheme={
                                  log.type === 'credit_added' ? 'green' : 
                                  log.type === 'call_charge' ? 'red' : 'blue'
                                }
                              >
                                {log.type === 'credit_added' ? 'זיכוי' :
                                 log.type === 'call_charge' ? 'חיוב' : 'אחר'}
                              </Badge>
                            </Td>
                            <Td 
                              color={log.type === 'credit_added' ? 'green.500' : 'red.500'}
                              fontWeight="medium"
                            >
                              {log.type === 'credit_added' ? '+' : '-'}{formatCurrency(log.amount)}
                            </Td>
                            <Td>{formatCurrency(log.previous_balance)}</Td>
                            <Td fontWeight="medium">{formatCurrency(log.new_balance)}</Td>
                            <Td>{log.description}</Td>
                            <Td fontSize="sm">{formatDateTime(log.timestamp)}</Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>
              </CardBody>
            </Card>
          </TabPanel>

          {/* הגדרות מערכת - מעודכן עם מצב חפירה */}
          <TabPanel>
            <VStack spacing={6} align="stretch">
              <Card variant="outline" borderColor={borderColor}>
                <CardHeader>
                  <Heading size="md">מחירון שיחות מעודכן</Heading>
                </CardHeader>
                <CardBody>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Box>
                      <Flex align="center" mb={2}>
                        <Icon as={MdPhone} color="blue.500" mr={2} />
                        <Text fontWeight="medium">שיחה רגילה:</Text>
                      </Flex>
                      <Text color="blue.500" fontSize="lg" fontWeight="bold">
                        {formatCurrency(CALL_PRICING.per_minute_answered_usd)}/דקה
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        רק על שיחות שנענו
                      </Text>
                    </Box>
                    <Box>
                      <Flex align="center" mb={2}>
                        <Icon as={MdLoop} color="purple.500" mr={2} />
                        <Text fontWeight="medium">מצב חפירה:</Text>
                      </Flex>
                      <Text color="green.500" fontSize="lg" fontWeight="bold">
                        {formatCurrency(CALL_PRICING.digging_mode_cost)}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        ללא חיוב כלל
                      </Text>
                    </Box>
                    <Box>
                      <Text fontWeight="medium">מינימום חיוב:</Text>
                      <Text color="blue.500" fontSize="lg">
                        {formatCurrency(CALL_PRICING.minimum_charge_usd)}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        דקה אחת (רק במצב רגיל)
                      </Text>
                    </Box>
                    <Box>
                      <Text fontWeight="medium">ניסיונות שיחה:</Text>
                      <Text color="green.500" fontSize="lg">
                        {formatCurrency(0)}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        ללא חיוב עד שנענה
                      </Text>
                    </Box>
                  </SimpleGrid>

                  <Alert status="info" mt={6} borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>מדיניות תמחור חדשה 🎉</AlertTitle>
                      <AlertDescription>
                        <VStack align="start" spacing={2} mt={2}>
                          <HStack>
                            <Icon as={MdPhone} color="blue.500" />
                            <Text>
                              <strong>מצב רגיל</strong>: חיוב רק על שיחות שנענו
                            </Text>
                          </HStack>
                          <HStack>
                            <Icon as={MdLoop} color="purple.500" />
                            <Text>
                              <strong>מצב חפירה</strong>: ללא חיוב כלל - לזיהוי מקבלי שיחות
                            </Text>
                          </HStack>
                          <HStack>
                            <Icon as={MdMoneyOff} color="green.500" />
                            <Text>
                              <strong>ניסיונות</strong>: ללא חיוב עד שהשיחה נענתה
                            </Text>
                          </HStack>
                          <HStack>
                            <Icon as={MdMoneyOff} color="green.500" />
                            <Text>
                              <strong>דמי הקמה</strong>: ללא דמי הקמה בכל המצבים
                            </Text>
                          </HStack>
                        </VStack>
                      </AlertDescription>
                    </Box>
                  </Alert>
                </CardBody>
              </Card>

              <Card variant="outline" borderColor={borderColor}>
                <CardHeader>
                  <Heading size="md">הגדרות מצב חפירה</Heading>
                </CardHeader>
                <CardBody>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Box>
                      <Text fontWeight="medium">אינטרוול שיחות:</Text>
                      <Text color="purple.500" fontSize="lg">5 שניות</Text>
                      <Text fontSize="sm" color="gray.600">
                        זמן בין שיחות
                      </Text>
                    </Box>
                    <Box>
                      <Text fontWeight="medium">זמן ניתוק אוטומטי:</Text>
                      <Text color="purple.500" fontSize="lg">2 שניות</Text>
                      <Text fontSize="sm" color="gray.600">
                        ניתוק אחרי מענה
                      </Text>
                    </Box>
                  </SimpleGrid>
                  
                  <Alert status="success" mt={4} borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>מצב חפירה פעיל</AlertTitle>
                      <AlertDescription>
                        מצב זה מאפשר למשתמשים לבצע שיחות חוזרות לזיהוי מקבלי שיחות
                        ללא כל עלות. מועיל לסקרים, מחקרים וזיהוי נוכחות.
                      </AlertDescription>
                    </Box>
                  </Alert>
                </CardBody>
              </Card>

              <Card variant="outline" borderColor={borderColor}>
                <CardHeader>
                  <Heading size="md">פעולות מערכת</Heading>
                </CardHeader>
                <CardBody>
                  <VStack spacing={4} align="start">
                    <Button colorScheme="blue" onClick={fetchData} leftIcon={<ViewIcon />}>
                      רענן נתונים
                    </Button>
                    <Button colorScheme="green" leftIcon={<EditIcon />}>
                      יצא דוח חיובים
                    </Button>
                    <Button colorScheme="purple" leftIcon={<EditIcon />}>
                      יצא דוח שיחות (כולל חפירה)
                    </Button>
                    <Button colorScheme="teal" leftIcon={<EditIcon />}>
                      סטטיסטיקות מצב חפירה
                    </Button>
                  </VStack>
                </CardBody>
              </Card>
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* מודל זיכוי - אותו דבר */}
      <Modal isOpen={isCreditOpen} onClose={onCreditClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>הוסף זיכוי למשתמש</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Text>
                משתמש: <strong>{selectedUser?.email}</strong>
              </Text>
              <Text>
                יתרה נוכחית: <strong>{formatCurrency(selectedUser?.credits)}</strong>
              </Text>
              <NumberInput 
                value={creditAmount} 
                onChange={(value) => setCreditAmount(Number(value))}
                min={0}
                precision={2}
                step={0.1}
              >
                <NumberInputField placeholder="סכום לזיכוי (בדולרים)" />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <Text fontSize="sm" color="gray.600">
                יתרה חדשה: {formatCurrency((selectedUser?.credits || 0) + creditAmount)}
              </Text>
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  זיכוי יחול רק על שיחות רגילות שנענו. מצב חפירה תמיד ללא חיוב.
                </AlertDescription>
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="green" onClick={addCredits} mr={3}>
              הוסף זיכוי
            </Button>
            <Button variant="outline" onClick={onCreditClose}>
              בטל
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* מודל ניהול פיצ'רים - אותו דבר */}
      <Modal isOpen={isFeatureOpen} onClose={onFeatureClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>ניהול פיצ'רים</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Text fontWeight="bold">
                משתמש: {selectedUser?.email}
              </Text>
              
              {selectedUser && Object.entries({
                unlimited_calls: 'שיחות ללא הגבלה',
                priority_support: 'תמיכה מועדפת',
                analytics: 'דוחות ואנליטיקה',
                bulk_operations: 'פעולות קבוצתיות',
                custom_messages: 'הודעות מותאמות',
                digging_mode: 'מצב חפירה מתקדם'
              }).map(([feature, description]) => (
                <HStack key={feature} justify="space-between" w="100%">
                  <Text>{description}</Text>
                  <Switch
                    isChecked={selectedUser.features?.[feature] || false}
                    onChange={() => toggleFeature(
                      selectedUser.id, 
                      feature, 
                      selectedUser.features?.[feature] || false
                    )}
                    colorScheme="teal"
                  />
                </HStack>
              ))}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onFeatureClose}>
              סגור
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AdminPanel;